import { Amount } from '@core/types/amount';
import { Address, Business } from './business';
import { ShippingFee } from '@monolith/legacy/types/shipping/shipping-fees';
import { InvitedStatusValue } from '@monolith/legacy/types/r2b';
import { Currency } from 'dinero.js';
import Country from '@core/types/country';
import { AutomaticOrderValidationMode } from '@monolith/legacy/types/fulfillment';

export interface Brand {
  type: 'brand';
  id: number;
  accepted_at: string;
  active: boolean;
  name: string;
  link: string;
  instagram_followers: number;
  instagram_username: string;
  enable_fast_payment: boolean;
  properties: {
    Location: string;
    Since: number;
    Distribution: string;
  };
  coming_soon: boolean;
  minimum: Amount;
  images: {
    squared: string;
    rounded: string;
    large: string;
  };
  tags: string[];
  story: string;
  story_auto_translate: boolean;
  story_original_content: string;
  opened_countries: string[];
  original_language: string;
  original_language_label: string;
  allowed_vat_rates?: string[];
  first_accepted_at: string;
  shipping_lead_time_details: {
    type: 'shipping_lead_time';
    name: string;
    minimum: number;
    maximum: number;
  };
  business_events: Array<{
    id: string;
    is_eligible: boolean;
    event_type: boolean;
    select_count: number;
    select: boolean;
  }>;
  monthly_follow_up: boolean;
  addresses: {
    billing: Address;
    shipping: Address;
  };
  lift_promocode: {
    id: number;
    code: string;
    expires_at: string;
    amount: Amount;
    minimum_order: Amount;
    minimum_checkout: Amount;
    retailer_id: number;
    brand_id: number;
  };
  business: Business;
  currency: Currency;
  stripe: {
    account_created: boolean;
    status: string;
  };
  pending_actions: {
    referrals: number;
    payments: number;
    events: number;
    messages: number;
  };
  payment_method_override: PaymentMethodOverride;
  observed_shipping_lead_time: string;
  made_in: string[];
  main_category: {
    id: number;
    name: string;
    path: string;
  };
  retailer_will_require_eori?: boolean;
  applicable_shipping_fees?: ShippingFee[];
  brand_tags: string[];
  has_new_products: boolean;
  has_product_collection: boolean;
  isOpenedInUserCountry?: boolean;
  candidate_brand?: {
    status: InvitedStatusValue;
  };
  event_special_discount?: number;
  fulfillment_enabled?: boolean;
  fulfillment_provider?: FulfillmentProvider | null;
  fulfillment_brand_uuid?: string;
  country?: Country;
  discount?: number;
  is_discovery: boolean;
  is_eligible_for_discovery_free_shipping: boolean;
  is_not_eligible_for_ak_plus_free_shipping: boolean;
  uuid?: string;
  automatic_order_validation_mode?: AutomaticOrderValidationMode;
  holiday_mode: {
    enabled: boolean;
    start_date?: string;
    end_date?: string;
  };
  partially_completed?: boolean;
  onboarding_steps?: Array<{
    name: string;
    complete: boolean;
  }>;
  minimum_order_values?: Array<{
    type: MinimumOrderType;
    value: Amount;
  }>;
}

export enum MinimumOrderType {
  FirstOrder = 'discovery',
  RepeatOrder = 'reorder',
}

export type BrandLight = Pick<Brand, 'id' | 'name' | 'link' | 'images' | 'holiday_mode'>;

export enum FulfillmentProvider {
  Ankorstore = 'ankorstore',
  Futurlog = 'futurlog',
}

type PaymentMethodOverride = 'sepa' | 'card' | null;

// Brand Reactivation

export enum BrandReactivationRequest {
  requestReactivation = 'request-reactivation',
  dismissBanner = 'dismiss-banner',
}

type BrandReactivationStatus = 'not_requested' | 'pending' | 'approved' | 'completed' | 'rejected';
type BrandStatus = 'offline' | 'online' | 'coming_soon';

export enum BrandReactivationStatusLabel {
  not_requested = 'not_requested',
  pending = 'pending',
  approved = 'approved',
  completed = 'completed',
  rejected = 'rejected',
}

export type BrandReactivationResponse = {
  data: {
    type: 'reactivations';
    id: string;
    attributes: {
      brandUuid: string;
      status: BrandReactivationStatus;
      previousStatus: BrandReactivationStatus;
      createdAt: string;
    };
    meta: {
      brandStatus: BrandStatus;
    };
  };
};
